import { capital, cutAt, firstLetter } from "./letters";

/**
 * The few ways a figure, a time or a text is said, shared by every
 * page. Pure.
 */

/** The first letter of a name, for its tile: one letter, never half of one. */
export function initialOf(name: string): string {
    return capital(firstLetter(name)) || "?";
}

const DAY_ONLY = /^\d{4}-\d{2}-\d{2}T00:00:00(?:\.0+)?(?:Z|\+00:00)$/;

const DAY_MS = 86_400_000;

/**
 * Whether a posting's date names a day and no time. Workday, Workable and
 * BambooHR state only a day, which the catalogue keeps as midnight UTC, so
 * read in the reader's zone it would fall a day early west of Greenwich.
 */
export function isDayOnly(value: string): boolean {
    return DAY_ONLY.test(value);
}

/** The day `date` falls on in `timeZone`, counted from the epoch. */
function dayNumber(date: Date, timeZone?: string): number {
    const parts = new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "numeric",
        day: "numeric",
        timeZone,
    }).formatToParts(date);
    const part = (type: string) => Number(parts.find((each) => each.type === type)?.value);
    return Date.UTC(part("year"), part("month") - 1, part("day")) / DAY_MS;
}

/**
 * How long ago a posting went up. One that states only a day is counted
 * in days from the reader's today, never in hours, and never ahead of it.
 */
export function postedAgo(value: string, now: Date = new Date(), timeZone?: string): string {
    if (!isDayOnly(value)) return formatRelative(value, now, timeZone);
    const days = dayNumber(now, timeZone) - Date.parse(value) / DAY_MS;
    if (Number.isNaN(days) || days >= 30) return formatRelative(value, now, "UTC");
    const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
    return formatter.format(-Math.max(0, days), "day");
}

/**
 * "4 minutes ago", "3 days ago", or the date once it is more than a month
 * back. Relative time is what a queue wants ("how long has this been
 * waiting"); a timestamp is what a record wants, and the timeline shows
 * both.
 */
export function formatRelative(
    value: string,
    now: Date = new Date(),
    timeZone?: string,
    locale?: string,
): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }

    const seconds = Math.round((now.getTime() - date.getTime()) / 1000);
    const formatter = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });

    // Each unit is chosen on the figure it would say, so 3,599 seconds is
    // "1 hour ago" and never "60 minutes ago".
    if (Math.abs(seconds) < 60) return formatter.format(-seconds, "second");
    const minutes = Math.round(seconds / 60);
    if (Math.abs(minutes) < 60) return formatter.format(-minutes, "minute");
    const hours = Math.round(seconds / 3600);
    if (Math.abs(hours) < 24) return formatter.format(-hours, "hour");
    const days = Math.round(seconds / 86_400);
    if (Math.abs(days) < 30) return formatter.format(-days, "day");

    return new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeZone }).format(date);
}

/** A count with a thousands separator, as the tiles want it. */
export function formatCount(value: number): string {
    return new Intl.NumberFormat(undefined).format(value);
}

/**
 * The first sentence or so of a description, for a card. Cut at a word,
 * never mid-word, and never longer than `max` characters.
 */
export function excerpt(text: string, max = 160): string {
    const clean = plainText(text);
    if (clean.length <= max) {
        return clean;
    }
    // Cut on a whole letter: half of an emoji draws as a box.
    const cut = cutAt(clean, max);
    const atWord = cut.lastIndexOf(" ");
    return `${(atWord > max / 2 ? cut.slice(0, atWord) : cut).trimEnd()}…`;
}

/** A description's words on one line, as an excerpt cuts them. */
export function plainText(text: string): string {
    // A list item's `- ` is the line's shape, not its words.
    return text
        .replace(/^[ \t]*- /gm, "")
        .replace(/\s+/g, " ")
        .trim();
}

/** A byte count the way a file dialog shows it: `48 KB`, `1.2 MB`. */
export function formatBytes(bytes: number): string {
    // A size that is not one (negative, NaN, endless) says nothing absurd.
    if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
    if (bytes < 1024) return `${bytes} B`;
    const kb = Math.round(bytes / 1024);
    // Just short of a megabyte rounds to 1024 KB, which is said as 1.0 MB.
    if (kb < 1024) return `${kb} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
